import { useNavigation } from 'expo-router/react-navigation';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';

export const useIdleTimeout = (
  timeoutDurationInMin = 10,
  onTimeout?: () => void,
) => {
  const timeoutDuration = timeoutDurationInMin * 60 * 1000;
  const [lastActiveTime, setLastActiveTime] = useState<number | null>(
    Date.now(),
  );

  const timerRef = useRef<NodeJS.Timeout | number | null>(null);
  const lastActiveTimeRef = useRef<number | null>(lastActiveTime);
  const { handleLogout } = useContext<AuthContextProps>(AuthContext);
  const navigation = useNavigation();

  const handleTimeout = useCallback(async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;

      const state = navigation.getState();
      const currentRouteName = state?.routes[state.index]?.name;

      onTimeout?.();

      if (currentRouteName !== '(public)') {
        await handleLogout({ hideBiometricButton: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTimeout]);

  const resetIdleTimer = useCallback(() => {
    const currentTime = Date.now();
    lastActiveTimeRef.current = currentTime;
    setLastActiveTime(currentTime);
  }, []);

  const clearIdleTimer = useCallback(() => {
    lastActiveTimeRef.current = null;
    setLastActiveTime(null);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const appStateListener = AppState.addEventListener(
      'change',
      (nextAppState) => {
        if (nextAppState === 'active') {
          resetIdleTimer();
        }
      },
    );

    return () => {
      appStateListener.remove();
    };
  }, [resetIdleTimer]);

  useEffect(() => {
    if (lastActiveTime === null) {
      return;
    }

    const checkIdleTimeout = setInterval(async () => {
      if (
        lastActiveTimeRef.current !== null &&
        Date.now() - lastActiveTimeRef.current > timeoutDuration
      ) {
        await handleTimeout();
      }
    }, 1000);

    timerRef.current = checkIdleTimeout;

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [lastActiveTime, timeoutDuration, handleTimeout]);

  return {
    resetIdleTimer,
    clearIdleTimer,
  };
};
